"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1061 — KUK programme hero.
 *
 * Port of kuk-campus-portal `components/Degree/Hero.tsx`: the three-slide carousel
 * at the top of every `/online-*` programme page, with the Apply and Brochure CTAs
 * and the accreditation rail.
 *
 * THREE SLIDES, THREE LAYOUTS — AS DATA. v1 does not vary the artwork alone: it
 * branches on `currentIndex === 0 | 1 | 2` and writes three different overlays, with
 * the copy for slides 2 and 3 hardcoded in the component (and then re-branched on
 * `slug === "online-mba-plus"` to swap that copy for one programme). Every slide is a
 * `metadata` row here, carrying its own layout, copy, chips and colours, so the three
 * frozen branches become three editable rows — and a fourth slide is a row, not a
 * fourth `else if`.
 *
 *   `left_card`   slide 1 — dark scrim, a translucent column on the left holding the
 *                 programme name, and the accreditation rail pinned to the right.
 *   `right_panel` slide 2 — a solid panel on the right half with chips above a
 *                 centred heading.
 *   `wide_scrim`  slide 3 — a pale scrim over the full frame with the chips and copy
 *                 aligned left.
 *
 * ACCREDITATION lives in `metadata_1`, and each slide chooses whether to show it
 * (`show_accreditation`) — v1 renders the rail on slide 1 only. Below 768px the rail
 * collapses into the single inline strip v1 draws inside the card, which is why the
 * badges are one list rather than two.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes 768px as
 * `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:` is ported as
 * `tab:` and its `lg:` as this app's `sm:` (1080px) — the nearest real breakpoint to
 * 1024px, and the one this app already uses for "desktop layout starts here".
 *
 * THE MARQUEE IS NOT HERE. v1 renders the promotional strip ("Admissions closing
 * soon") inside this same component. This app already treats that strip as its own
 * page section (Header 5016), so it can be reordered, reworded or hidden without
 * touching the hero — the same split Banner 1051 makes on the home page.
 *
 * NOT CARRIED OVER — v1's `isUserVerified` branch, which swaps Apply for a "Continue
 * Application" link to the admission portal, and its `slug !== "online-ma"` /
 * `getPath()` guards that hide the Brochure button on some routes. Both are
 * per-visitor session state or per-route hardcoding, not page content: an editor who
 * wants the link CTA turns `apply_now_is_cta` off and sets `apply_now_link`, and one
 * who has no brochure turns `download_brochure_show` off.
 */

/** Slide layouts, matching v1's three `currentIndex` branches. */
type SlideLayout = "left_card" | "right_panel" | "wide_scrim";

interface HeroSlide {
  id?: string | number;
  image?: string;
  alt_image?: string;
  /** Optional; falls back to `image` when empty. */
  mobile_image?: string;
  mobile_alt_image?: string;
  layout?: SlideLayout;
  /** Full-frame overlay over the artwork. */
  scrim_color?: string;
  /** Fill of this slide's card / panel. */
  panel_color?: string;
  /**
   * `right_panel` only, below 768px, where v1's panel covers the whole frame and
   * is translucent so the photograph still reads through it. Falls back to
   * `panel_color`.
   */
  panel_color_mobile?: string;
  /**
   * `right_panel` only: how much of the width the panel takes from 768px up, as a
   * percentage. The artwork is sized to the REMAINDER rather than being covered —
   * that is what v1 does, and it changes how the photo is cropped.
   */
  panel_width?: number | string;
  /** Large line — the programme's short name on slide 1. */
  title_1?: string;
  title_1_color?: string;
  /** Supporting line under it. */
  title?: string;
  title_color?: string;
  description?: string;
  description_color?: string;
  /** Comma-separated pills above the heading (v1's `bannerN_additional_info`). */
  chips?: string;
  chip_color?: string;
  chip_text_color?: string;
  /** Small pill under the CTAs (v1's `banner_additional_info`). */
  info_bar?: string;
  info_bar_color?: string;
  info_bar_text_color?: string;
  /** Apply CTA colours for this slide; fall back to the section's. */
  cta_background_color?: string;
  cta_text_color?: string;
  /** Brochure CTA colours for this slide; fall back to the section's. */
  brochure_background_color?: string;
  brochure_text_color?: string;
  show_accreditation?: boolean;
  priority?: number;
  status?: boolean;
}

interface AccreditationRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface Banner1061Data {
  /** Carousel slides. */
  metadata?: HeroSlide[];
  /** Accreditation badges. */
  metadata_1?: AccreditationRow[];
  /** Autoplay interval in ms. */
  slide_frequency?: number;
  /** Desktop height in px. 0 (the default) keeps v1's viewport-relative height. */
  hero_section_height?: number;
  /** Accreditation rail fill. */
  background_color?: string;
  /** Accreditation label colour. */
  title_color?: string;
  /** Rule between accreditation badges. */
  divider_color?: string;
  /** Prev / next arrow pill background. */
  carousel_color?: string;
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  /** Apply CTA fill / label, used by any slide that sets no colours of its own. */
  button_color?: string;
  apply_now_color?: string;
  download_brochure_show?: boolean;
  download_brochure_text?: string;
  download_brochure_link?: string;
  /** On → the Brochure button opens the download lead form instead of linking. */
  download_brochure_is_cta?: boolean;
  /** Brochure CTA fill, used by any slide that sets no colours of its own. */
  apply_now_border_color?: string;
  /** Brochure CTA icon. */
  icon?: string;
  status?: boolean;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function sortActive<T extends { priority?: number; status?: boolean }>(
  rows: T[] | undefined,
): T[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r?.status !== false)
    .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));
}

/** v1 splits `bannerN_additional_info` on commas and drops the blanks. */
function splitChips(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
}

const Banner1061 = (props: { data?: Banner1061Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1061Data | undefined;
  const dispatch = useDispatch();
  const [index, setIndex] = useState(0);

  const {
    slide_frequency = 5000,
    hero_section_height = 0,
    background_color = "rgba(255, 255, 255, 0.3)",
    title_color = "#031F44",
    divider_color = "#FFFFFF",
    carousel_color = "rgba(0, 0, 0, 0.5)",
    apply_now_text = "Apply",
    apply_now_link = "",
    button_color = "#9F1C33",
    apply_now_color = "#FFFFFF",
    download_brochure_text = "Brochure",
    download_brochure_link = "",
    apply_now_border_color = "rgba(255, 255, 255, 0.34)",
    icon = "download",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const brochureShow = isCmsShowFlagOn(sectionData?.download_brochure_show, true);
  const brochureIsCta = isCmsShowFlagOn(
    sectionData?.download_brochure_is_cta,
    true,
  );
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const slides = useMemo(
    () =>
      sortActive(sectionData?.metadata).filter(
        (s) => !!(s?.image || s?.mobile_image),
      ),
    [sectionData?.metadata],
  );
  const n = slides.length;

  const badges = useMemo(() => {
    const rows = sectionData?.metadata_1;
    if (!Array.isArray(rows)) return [] as AccreditationRow[];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_image || r?.meta_title))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata_1]);

  // v1 advances on a fixed interval with no pause-on-hover.
  useEffect(() => {
    if (!status || n <= 1 || !slide_frequency) return undefined;
    const id = window.setInterval(
      () => setIndex((prev) => (prev === n - 1 ? 0 : prev + 1)),
      Number(slide_frequency),
    );
    return () => window.clearInterval(id);
  }, [status, n, slide_frequency]);

  // A slide count change (CMS edit / preview) can leave `index` past the end.
  useEffect(() => {
    if (n > 0 && index > n - 1) setIndex(0);
  }, [n, index]);

  const prevSlide = useCallback(
    () => setIndex((p) => (p === 0 ? n - 1 : p - 1)),
    [n],
  );
  const nextSlide = useCallback(
    () => setIndex((p) => (p === n - 1 ? 0 : p + 1)),
    [n],
  );

  const handleApplyClick = useCallback(() => {
    ctaClick({
      section: "banner_1061",
      component: "hero_apply",
      label: apply_now_text?.trim() || "apply",
      step: "1",
      entity: "banner_1061",
      parentEntity: "banner_1061",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  const handleBrochureClick = useCallback(() => {
    ctaClick({
      section: "banner_1061",
      component: "hero_download_brochure",
      label: download_brochure_text?.trim() || "brochure",
      step: "1",
      entity: "banner_1061",
      parentEntity: "banner_1061",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.DOWNLOAD }));
  }, [download_brochure_text, dispatch]);

  if (!status || n === 0) return null;

  const pinnedHeight = Number(hero_section_height) > 0;

  const ctaClassName = cn(
    "inline-flex h-[47px] w-[200px] items-center justify-center gap-2",
    "whitespace-nowrap rounded-md px-4 text-base font-light shadow transition-colors",
    "tab:w-[170px] sm:w-[200px]",
  );

  /** One CTA: a lead-form button, or a link when the CMS supplies a URL instead. */
  const renderCta = (
    label: string,
    style: { backgroundColor: string; color: string },
    isCta: boolean,
    href: string,
    onClick: () => void,
    iconName?: string,
  ) => {
    const inner = (
      <>
        {iconName ? (
          <DynamicIcon
            iconName={iconName}
            width="18"
            height="18"
            color={style.color}
          />
        ) : null}
        <span>{label}</span>
      </>
    );
    return isCta || !href ? (
      <button
        type="button"
        onClick={onClick}
        className={ctaClassName}
        style={style}
      >
        {inner}
      </button>
    ) : (
      <Link href={normalizeHref(href)} className={ctaClassName} style={style}>
        {inner}
      </Link>
    );
  };

  const renderCtas = (slide: HeroSlide, align: "center" | "start") => {
    const applyStyle = {
      backgroundColor: slide.cta_background_color || button_color,
      color: slide.cta_text_color || apply_now_color,
    };
    const brochureStyle = {
      backgroundColor: slide.brochure_background_color || apply_now_border_color,
      color: slide.brochure_text_color || apply_now_color,
    };
    const showApply = applyNowShow && !!apply_now_text;
    const showBrochure = brochureShow && !!download_brochure_text;
    if (!showApply && !showBrochure) return null;
    return (
      <div
        className={cn(
          "mt-6 flex flex-col gap-3 sm:flex-row sm:gap-2",
          align === "center" ? "items-center" : "items-start",
        )}
      >
        {showApply
          ? renderCta(
              apply_now_text,
              applyStyle,
              applyNowIsCta,
              apply_now_link,
              handleApplyClick,
            )
          : null}
        {showBrochure
          ? renderCta(
              download_brochure_text,
              brochureStyle,
              brochureIsCta,
              download_brochure_link,
              handleBrochureClick,
              icon,
            )
          : null}
      </div>
    );
  };

  const renderChips = (slide: HeroSlide, align: "center" | "start") => {
    const chips = splitChips(slide.chips);
    if (chips.length === 0) return null;
    return (
      <div
        className={cn(
          "flex flex-col flex-wrap gap-3 tab:flex-row",
          align === "center" ? "items-center justify-center" : "items-start",
        )}
      >
        {chips.map((chip, i) => (
          <span
            key={`${chip}-${i}`}
            className="p-5 text-sm font-medium tab:text-lg"
            style={{
              backgroundColor: slide.chip_color || "#EFAC6A",
              color: slide.chip_text_color || "#031F44",
            }}
          >
            {chip}
          </span>
        ))}
      </div>
    );
  };

  const renderInfoBar = (slide: HeroSlide) =>
    slide.info_bar ? (
      <span
        className="mt-4 rounded-sm p-1 text-xs tab:text-sm"
        style={{
          backgroundColor: slide.info_bar_color || "rgba(255, 255, 255, 0.16)",
          color: slide.info_bar_text_color || "#FFFFFF",
        }}
      >
        {slide.info_bar}
      </span>
    ) : null;

  /**
   * The badge strip v1 draws inside the slide-1 card below 768px — one row of
   * badge + first word, hairline-separated.
   */
  const renderInlineBadges = (panelFill: string) =>
    badges.length === 0 ? null : (
      <div
        className="flex w-full items-center justify-between rounded-md px-5 py-2 tab:hidden"
        style={{ backgroundColor: panelFill }}
      >
        {badges.map((badge, i) => (
          <div key={badge.id ?? i} className="flex items-center gap-2">
            {badge.meta_image ? (
              <Image
                src={badge.meta_image}
                alt={badge.meta_alt_image || badge.meta_title || "Accreditation"}
                width={15}
                height={15}
                className="h-auto w-auto"
              />
            ) : null}
            <p className="text-xs" style={{ color: title_color }}>
              {(badge.meta_title || "").split(" ")[0]}
            </p>
            {i !== badges.length - 1 ? (
              <span
                aria-hidden
                className="mx-2 h-4 w-0.5"
                style={{ backgroundColor: divider_color }}
              />
            ) : null}
          </div>
        ))}
      </div>
    );

  /** The vertical rail v1 pins to the right of slide 1 from 768px up. */
  const renderBadgeRail = () =>
    badges.length === 0 ? null : (
      <div
        className="absolute right-10 top-0 hidden h-full w-[10%] tab:block"
        style={{ backgroundColor: background_color }}
      >
        <div className="flex size-full flex-col items-center justify-center gap-10">
          {badges.map((badge, i) => (
            <div
              key={badge.id ?? i}
              className="flex w-full flex-col items-center justify-center gap-2"
            >
              {badge.meta_image ? (
                <Image
                  src={badge.meta_image}
                  alt={badge.meta_alt_image || badge.meta_title || "Accreditation"}
                  width={40}
                  height={40}
                  className="h-auto w-auto"
                />
              ) : null}
              {badge.meta_title ? (
                <p
                  className="text-center text-sm font-bold"
                  style={{ color: title_color }}
                >
                  {badge.meta_title}
                </p>
              ) : null}
              {i !== badges.length - 1 ? (
                <span
                  aria-hidden
                  className="mt-8 h-0.5 w-4/5"
                  style={{ backgroundColor: divider_color }}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );

  const renderOverlay = (slide: HeroSlide) => {
    const layout: SlideLayout = slide.layout || "left_card";
    const showBadges = isCmsShowFlagOn(
      slide.show_accreditation,
      layout === "left_card",
    );

    if (layout === "right_panel") {
      return (
        <>
          {slide.scrim_color ? (
            <div
              aria-hidden
              className="absolute inset-0"
              style={{ backgroundColor: slide.scrim_color }}
            />
          ) : null}
          <div
            className={cn(
              "absolute inset-0 z-20",
              // Below 768px v1's panel covers the whole frame; above it the panel is
              // beside the artwork, not over it.
              "tab:left-auto tab:right-0 tab:w-[var(--kuk-panel-w)]",
            )}
          >
            {/*
              Two fills rather than one background that swaps at `tab`: the panel is
              opaque above 768px but translucent below it, where it covers the whole
              frame and the photograph has to read through. A single background
              utility built from a CSS variable would need a type hint to compile at
              all; static classes plus inline colours cannot silently drop out.
            */}
            <div
              aria-hidden
              className="absolute inset-0 tab:hidden"
              style={{
                backgroundColor:
                  slide.panel_color_mobile || slide.panel_color || "#1C3557",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 hidden tab:block"
              style={{ backgroundColor: slide.panel_color || "#1C3557" }}
            />
            {/* `relative` so the copy paints above the two absolute fills. */}
            <div
              className={cn(
                "relative flex size-full flex-col items-center justify-center px-10",
                "tab:px-20",
              )}
            >
              {renderChips(slide, "center")}
              {slide.title_1 ? (
                <h1
                  className="mt-8 text-center text-3xl font-bold tab:text-4xl"
                  style={{
                    color: slide.title_1_color || "#FFFFFF",
                  }}
                >
                  {slide.title_1}
                </h1>
              ) : null}
              {slide.title ? (
                <h2
                  className="mt-4 text-center text-base font-light"
                  style={{
                    color: slide.title_color || "#FFFFFF",
                  }}
                >
                  {slide.title}
                </h2>
              ) : null}
              {slide.description ? (
                <p
                  className="mt-4 text-center text-sm font-light"
                  style={{
                    color: slide.description_color || "rgba(255,255,255,0.7)",
                  }}
                >
                  {slide.description}
                </p>
              ) : null}
              {renderCtas(slide, "center")}
              {renderInfoBar(slide)}
            </div>
          </div>
        </>
      );
    }

    if (layout === "wide_scrim") {
      return (
        <>
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundColor: slide.scrim_color || "rgba(255, 255, 255, 0.6)",
            }}
          />
          <div
            className={cn(
              // Mobile: full frame, vertically centred, 20px gutter.
              "absolute inset-0 z-20 flex flex-col items-start justify-center px-5",
              // Desktop: v1 pins the copy a third of the way down and offsets it.
              "tab:inset-y-auto tab:top-1/3 tab:left-10 tab:h-auto tab:justify-start tab:px-20",
            )}
          >
            {renderChips(slide, "start")}
            {slide.title_1 ? (
              <h1
                className="mt-8 text-3xl font-bold tab:text-5xl"
                style={{
                  color: slide.title_1_color || "#031F44",
                }}
              >
                {slide.title_1}
              </h1>
            ) : null}
            {slide.title ? (
              <h2
                className="mt-4 text-base font-light"
                style={{
                  color: slide.title_color || "#031F44",
                }}
              >
                {slide.title}
              </h2>
            ) : null}
            {slide.description ? (
              <p
                className="mt-4 max-w-[640px] text-sm font-light"
                style={{
                  color: slide.description_color || "#031F44",
                }}
              >
                {slide.description}
              </p>
            ) : null}
            {renderCtas(slide, "start")}
            {renderInfoBar(slide)}
          </div>
        </>
      );
    }

    // left_card
    return (
      <>
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundColor: slide.scrim_color || "rgba(0, 0, 0, 0.5)",
          }}
        />
        {showBadges ? renderBadgeRail() : null}
        <div
          className={cn(
            "absolute inset-y-0 left-5 z-20 w-80",
            "tab:left-36 tab:w-60",
            "sm:left-44 sm:w-[32%]",
          )}
          style={{
            backgroundColor: slide.panel_color || "rgba(255, 255, 255, 0.2)",
          }}
        >
          <div className="flex h-full items-center justify-center px-5">
            <div className="flex flex-col items-center justify-center gap-2 text-center">
              {slide.title_1 ? (
                <h1
                  className={cn(
                    "text-5xl font-bold",
                    // v1 shrinks a long name below 768px only; desktop stays 5xl.
                    (slide.title_1?.length || 0) > 20 && "max-[767px]:text-4xl",
                  )}
                  style={{
                    color: slide.title_1_color || "#FFFFFF",
                  }}
                >
                  {slide.title_1}
                </h1>
              ) : null}
              {slide.title ? (
                <h2
                  className="text-base font-light"
                  style={{
                    color: slide.title_color || "#FFFFFF",
                  }}
                >
                  {slide.title}
                </h2>
              ) : null}
              {slide.description ? (
                <p
                  className="text-sm font-light"
                  style={{
                    color: slide.description_color || "#FFFFFF",
                  }}
                >
                  {slide.description}
                </p>
              ) : null}
              {showBadges
                ? renderInlineBadges(
                    slide.panel_color || "rgba(255, 255, 255, 0.2)",
                  )
                : null}
              {renderCtas(slide, "center")}
              {renderInfoBar(slide)}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <section
      className={cn(
        "relative w-full overflow-hidden",
        // v1: 60vh below 768px; 75vh above it, or 90vh when the window is short.
        pinnedHeight
          ? "h-[60vh] tab:h-[var(--kuk-hero-h)]"
          : "h-[60vh] tab:h-[75vh] tab:[@media(max-height:799px)]:h-[90vh]",
      )}
      style={
        pinnedHeight
          ? ({
              ["--kuk-hero-h" as string]: `${hero_section_height}px`,
            } as any)
          : undefined
      }
    >
      <div
        className="flex size-full transition-transform duration-700 ease-in-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((slide, i) => {
          const desktopSrc = slide.image || slide.mobile_image || "";
          const mobileSrc = slide.mobile_image || slide.image || "";
          const isFirst = i === 0;
          const isSplit = (slide.layout || "left_card") === "right_panel";
          const panelPct = Math.min(
            95,
            Math.max(0, Number(slide.panel_width) || 40),
          );

          return (
            <div
              key={slide.id ?? i}
              className="relative size-full shrink-0"
              style={
                isSplit
                  ? ({
                      ["--kuk-panel-w" as string]: `${panelPct}%`,
                      ["--kuk-art-w" as string]: `${100 - panelPct}%`,
                    } as any)
                  : undefined
              }
            >
              <div
                className={cn(
                  "h-full w-full",
                  // v1 narrows the photograph so the panel sits BESIDE it. Covering
                  // a full-width photo instead would crop it differently.
                  isSplit && "tab:w-[var(--kuk-art-w)]",
                )}
              >
                <Image
                  src={desktopSrc}
                  alt={slide.alt_image || `Slide ${i + 1}`}
                  width={1920}
                  height={900}
                  priority={isFirst}
                  loading={isFirst ? "eager" : "lazy"}
                  quality={85}
                  fetchPriority={isFirst ? "high" : "auto"}
                  sizes="100vw"
                  className="hidden size-full object-cover object-top tab:block"
                />
                <Image
                  src={mobileSrc}
                  alt={
                    slide.mobile_alt_image || slide.alt_image || `Slide ${i + 1}`
                  }
                  width={800}
                  height={600}
                  priority={isFirst}
                  loading={isFirst ? "eager" : "lazy"}
                  quality={85}
                  fetchPriority={isFirst ? "high" : "auto"}
                  sizes="100vw"
                  className="size-full object-cover object-top tab:hidden"
                />
              </div>
              {renderOverlay(slide)}
            </div>
          );
        })}
      </div>

      {/* v1 shows the arrows only with more than one slide. */}
      {n > 1 ? (
        <>
          <button
            type="button"
            onClick={prevSlide}
            aria-label="Previous slide"
            className={cn(
              "absolute left-5 top-1/2 z-30 hidden size-10 -translate-y-1/2",
              "items-center justify-center rounded-full text-white tab:flex",
            )}
            style={{ backgroundColor: carousel_color }}
          >
            &#10094;
          </button>
          <button
            type="button"
            onClick={nextSlide}
            aria-label="Next slide"
            className={cn(
              "absolute right-5 top-1/2 z-30 hidden size-10 -translate-y-1/2",
              "items-center justify-center rounded-full text-white tab:flex",
            )}
            style={{ backgroundColor: carousel_color }}
          >
            &#10095;
          </button>
        </>
      ) : null}
    </section>
  );
};

export default Banner1061;
